import React, { useContext } from 'react'
import { array, string, bool, func } from 'prop-types'
import styled, { css } from 'styled-components'
import { rgba, stripUnit } from 'polished'

import { SWATCH_COLOR_FOR_OPTION_NAME } from 'utils/constants'
import { availableForSale } from 'utils/products'

import CheckoutContext from 'contexts/Checkout'
import { black, white } from 'styles/colors'
import NativeAuthContext from 'components/NativeAuth'
import { square, animationTime, animationCurve } from 'styles/global'
import { H5, fontSizes } from 'styles/typography'
import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'

import Icon from 'components/Icon'
import LockIcon from 'icons/lock.svg'

const Container = styled.div`
  position: static;
`

const Inner = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -${stripUnit(spacing.small) * 1.5 + 'px'};

  ${media.large`
    margin: -${stripUnit(spacing.medium) * 1.25 + 'px'};
  `};

  ${media.xLarge`
    margin: -${spacing.large};
  `};
`

const Section = styled.div`
  position: relative;
  padding: ${stripUnit(spacing.small) * 1.5 + 'px'};

  ${media.large`
    padding: ${stripUnit(spacing.medium) * 1.25 + 'px'};
  `};

  ${media.xLarge`
    padding: ${spacing.large};
  `};
`

const List = styled.div`
  display: flex;
`

const SectionHeading = styled(H5)`
  margin-bottom: ${spacing.medium};

  ${mediaDown.medium`
    font-size: ${fontSizes.small};
    margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};
`

const SwatchItem = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;

  ${props =>
    props.disabled &&
    css`
      position: relative;

      &:after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 1px;
        height: 130%;
        background-color: ${rgba(black, 0.2)};
        transform: translateY(-50%) translateX(-50%) rotate(45deg);
      }
    `}

  & + & {
    margin-left: ${spacing.medium};

    ${props =>
      props.large &&
      css`
        margin-left: ${spacing.xxLarge};
      `}
  }
`

const Swatch = styled.button`
  position: relative;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${props => props.backgroundColor};
  ${square('32px')};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.3;
    `}

  ${props =>
    props.large &&
    css`
      background-image: url('${props => props.backgroundImage}');
      margin-bottom: ${spacing.xLarge}; /* Room for label */
      ${square('56px')};
    `}

  ${props =>
    props.small &&
    css`
      ${square('26px')};
    `}

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid ${rgba(black, 0.1)};
  }

  &:before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 2px solid ${black};
    border-radius: 50%;
    opacity: 0;
    visibility: hidden;
    transition: all ${animationTime} ${animationCurve};

    ${props =>
      props.active &&
      css`
        opacity: 1;
        visibility: visible;
      `}
  }
`

const SwatchLabel = styled.span`
  font-size: ${fontSizes.tiny};
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: -20px;
  text-align: center;
`

const Lock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${white};
  z-index: 10;
`

const BuyDevices = ({ devices, collectionTitle, handleDeviceSelect, selectedVariantId, small, isLocked }) => {
  const { products } = useContext(CheckoutContext)
  const { province } = useContext(NativeAuthContext)

  return (
    <Container>
      <Inner>
        {devices.map((device, idx) => {
          const fetchedProduct = products.find(p => device.id.includes(p.shopifyGid))

          return (
            <Section key={idx}>
              {collectionTitle === 'epod' && <SectionHeading>{device.title}</SectionHeading>}
              <List>
                {device.variants.map(v => {
                  const variant = fetchedProduct?.variants.find(variant => v.id.includes(variant.shopifyGid))
                  const isAvailableForSale = availableForSale({ variant, province })

                  return (
                    <SwatchItem
                      data-tip={availableForSale ? v.title : `${v.title} (Out of stock)`}
                      key={v.title}
                      disabled={!isAvailableForSale}
                      large={v.title === 'Mango Wonder' || v.title === 'Polar Mint'}
                    >
                      {isLocked && (
                        <Lock>
                          <Icon icon={<LockIcon />} />
                        </Lock>
                      )}
                      <Swatch
                        disabled={!availableForSale || isLocked}
                        backgroundColor={SWATCH_COLOR_FOR_OPTION_NAME[v.title.toLowerCase()]}
                        backgroundImage={`${
                          process.env.BASE_PATH
                        }/images/products/epod/droplets/${v.title.toLowerCase().replace(/ /g, '-')}.png`}
                        active={v.quantity === 1 || selectedVariantId === v.id}
                        small={small}
                        large={
                          v.title === 'Mango Wonder' ||
                          v.title === 'Polar Mint' ||
                          collectionTitle === 'limited-edition-epod'
                        }
                        onClick={() => {
                          handleDeviceSelect(v.id)
                        }}
                      />
                      {(v.title === 'Mango Wonder' || v.title === 'Polar Mint') && <SwatchLabel>{v.title}</SwatchLabel>}
                    </SwatchItem>
                  )
                })}
              </List>
            </Section>
          )
        })}
      </Inner>
    </Container>
  )
}

BuyDevices.propTypes = {
  devices: array.isRequired,
  collectionTitle: string,
  handleDeviceSelect: func,
  selectedVariantId: string,
  small: bool,
  isLocked: bool
}

export default BuyDevices
